
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    Grid,
    IconButton,
    Paper,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentIcon from '@mui/icons-material/Payment';

// ===== API helper (fallback). Você pode passar um apiFetch por props para reutilizar o do FinanceHome
const API_URL = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.REACT_APP_API_BACKEND)
    || (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_BACKEND)
    || 'http://localhost:3000';

async function defaultApiFetch(path, options = {}) {
    const res = await fetch(`${API_URL}${path}`, {
        headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
        ...options,
    });
    if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
    const ct = res.headers.get('content-type') || '';
    return ct.includes('application/json') ? res.json() : res.text();
}

// ===== CSV util simples
function exportToCsv(filename, rows) {
    if (!rows?.length) return;
    const keys = Object.keys(rows[0]);
    const esc = (v) => {
        if (v == null) return '';
        const s = String(v).replace(/"/g, '""');
        return s.includes(',') || s.includes('') ? `"${s}"` : s;
    };
    const csv = [keys.join(','), ...rows.map(r => keys.map(k => esc(r[k])).join(','))].join('');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Drawer lateral de detalhes de Vendas / Despesas / Pagamentos
 * Props:
 * - open, onClose
 * - cicloId (obrigatório para carregar)
 * - defaultTab: 'vendas' | 'despesas' | 'pagamentos'
 * - apiFetch?: função (opcional) para usar a mesma do FinanceHome
 * - caminhaoLabel?: string (opcional)
 */
export default function FinanceDetailsDrawer({ open, onClose, cicloId, defaultTab = 'vendas', apiFetch, caminhaoLabel }) {
    const _api = apiFetch || defaultApiFetch;

    const [tab, setTab] = useState(defaultTab); // 'vendas' | 'despesas' | 'pagamentos'
    useEffect(() => { setTab(defaultTab); }, [defaultTab, open]);

    // ===== estados: vendas
    const [vendasMes, setVendasMes] = useState(null); // { dias: [{data,total}], total }
    const [vendasFormas, setVendasFormas] = useState(null); // { formas: [{codigo,descricao,total}], totalGeral }
    const [vendasDia, setVendasDia] = useState(null); // { itens: [...], total }

    // ===== estados: despesas
    const [despesasMes, setDespesasMes] = useState(null);
    const [despesasDia, setDespesasDia] = useState(null);

    // ===== estados: pagamentos
    const [pagamentosMes, setPagamentosMes] = useState(null);
    const [pagamentosDia, setPagamentosDia] = useState(null);

    // ===== controles
    const [loadingMes, setLoadingMes] = useState(false);
    const [loadingDia, setLoadingDia] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null); // YYYY-MM-DD

    const currency = (v) => (v == null ? '-' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));

    // ===== carregamentos
    const loadVendasMes = async () => {
        if (!cicloId) return;
        setLoadingMes(true);
        try {
            const data = await _api(`/finance/vendas-mes?cicloId=${cicloId}&includeZeros=1`);
            setVendasMes(data);
            const formas = await _api(`/finance/vendas-mes-por-forma?cicloId=${cicloId}`);
            setVendasFormas(formas);
        } catch (e) { console.error(e); }
        finally { setLoadingMes(false); }
    };

    const loadDespesasMes = async () => {
        if (!cicloId) return;
        setLoadingMes(true);
        try {
            const data = await _api(`/finance/despesas-mes?cicloId=${cicloId}&includeZeros=1`);
            setDespesasMes(data);
        } catch (e) { console.error(e); }
        finally { setLoadingMes(false); }
    };

    const loadPagamentosMes = async () => {
        if (!cicloId) return;
        setLoadingMes(true);
        try {
            const data = await _api(`/finance/pagamentos-mes?cicloId=${cicloId}&includeZeros=1`);
            setPagamentosMes(data);
        } catch (e) { console.error(e); }
        finally { setLoadingMes(false); }
    };

    const loadVendasDia = async (date) => {
        if (!date || !cicloId) return;
        setLoadingDia(true);
        try {
            const data = await _api(`/finance/vendas-dia?cicloId=${cicloId}&data=${date}`);
            setVendasDia(data);
        } catch (e) { console.error(e); }
        finally { setLoadingDia(false); }
    };

    const loadDespesasDia = async (date) => {
        if (!date || !cicloId) return;
        setLoadingDia(true);
        try {
            const data = await _api(`/finance/despesas-dia?cicloId=${cicloId}&data=${date}`);
            setDespesasDia(data);
        } catch (e) { console.error(e); }
        finally { setLoadingDia(false); }
    };

    const loadPagamentosDia = async (date) => {
        if (!date || !cicloId) return;
        setLoadingDia(true);
        try {
            const data = await _api(`/finance/pagamentos-dia?cicloId=${cicloId}&data=${date}`);
            setPagamentosDia(data);
        } catch (e) { console.error(e); }
        finally { setLoadingDia(false); }
    };

    useEffect(() => {
        if (!open) return;
        if (tab === 'vendas') loadVendasMes();
        if (tab === 'despesas') loadDespesasMes();
        if (tab === 'pagamentos') loadPagamentosMes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, tab, cicloId]);

    const onRowClick = (date) => {
        setSelectedDate(date);
        if (tab === 'vendas') { setDespesasDia(null); setPagamentosDia(null); loadVendasDia(date); }
        else if (tab === 'despesas') { setVendasDia(null); setPagamentosDia(null); loadDespesasDia(date); }
        else { setVendasDia(null); setDespesasDia(null); loadPagamentosDia(date); }
    };

    const header = (
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="tabs">
                <Tab icon={<MonetizationOnIcon />} iconPosition="start" label="Vendas" value="vendas" />
                <Tab icon={<ReceiptLongIcon />} iconPosition="start" label="Despesas" value="despesas" />
                <Tab icon={<PaymentIcon />} iconPosition="start" label="Pagamentos" value="pagamentos" />
            </Tabs>
            <Stack direction="row" spacing={1} alignItems="center">
                {caminhaoLabel && <Chip size="small" label={caminhaoLabel} />}
                {cicloId && <Chip size="small" color="primary" label={`Ciclo ${cicloId}`} />}
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </Stack>
        </Box>
    );

    // ===== VENDAS VIEW =====
    const vendasView = (
        <Box sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="stretch">
                <Grid item xs={12} md={5} lg={4}>
                    <Paper sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle1">Vendas por dia</Typography>
                            <Stack direction="row" spacing={1}>
                                <Button size="small" onClick={() => vendasMes?.dias && exportToCsv(`vendas_${cicloId}.csv`, vendasMes.dias)}>Exportar CSV</Button>
                            </Stack>
                        </Box>
                        {loadingMes ? (
                            <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Data</TableCell>
                                        <TableCell align="right">Total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {vendasMes?.dias?.map((d) => (
                                        <TableRow hover key={d.data} onClick={() => onRowClick(d.data)} sx={{ cursor: 'pointer' }}>
                                            <TableCell>{d.data}</TableCell>
                                            <TableCell align="right">{currency(d.total)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="body2" color="text.secondary">Total do mês: <b>{currency(vendasMes?.total || 0)}</b></Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={7} lg={8}>
                    <Paper sx={{ p: 2, borderRadius: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>Detalhe do dia {selectedDate ? `(${selectedDate})` : ''}</Typography>
                        {loadingDia ? (
                            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
                        ) : !selectedDate ? (
                            <Typography variant="body2" color="text.secondary">Selecione um dia na tabela ao lado para ver as formas de pagamento.</Typography>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Forma</TableCell>
                                        <TableCell align="right">Valor</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {vendasDia?.itens?.map((it) => (
                                        <TableRow key={it.forma_pagamento_id}>
                                            <TableCell>{it.descricao || it.codigo}</TableCell>
                                            <TableCell align="right">{currency(it.valor)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="body2" color="text.secondary">Total do dia: <b>{currency(vendasDia?.total || 0)}</b></Typography>
                    </Paper>
                    <Paper sx={{ p: 2, borderRadius: 3, mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle1">Vendas por forma (mês)</Typography>
                            <Button size="small" onClick={() => vendasFormas?.formas && exportToCsv(`vendas_formas_${cicloId}.csv`, vendasFormas.formas)}>Exportar CSV</Button>
                        </Box>
                        {loadingMes ? (
                            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Forma</TableCell>
                                        <TableCell align="right">Total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {vendasFormas?.formas?.map((f) => (
                                        <TableRow key={f.forma_pagamento_id || f.codigo}>
                                            <TableCell>{f.descricao || f.codigo}</TableCell>
                                            <TableCell align="right">{currency(f.total)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="body2" color="text.secondary">Total do mês: <b>{currency(vendasFormas?.totalGeral || 0)}</b></Typography>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );

    // ===== DESPESAS VIEW =====
    const despesasView = (
        <Box sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="stretch">
                <Grid item xs={12} md={5} lg={4}>
                    <Paper sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle1">Despesas por dia</Typography>
                            <Stack direction="row" spacing={1}>
                                <Button size="small" onClick={() => despesasMes?.dias && exportToCsv(`despesas_${cicloId}.csv`, despesasMes.dias)}>Exportar CSV</Button>
                            </Stack>
                        </Box>
                        {loadingMes ? (
                            <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Data</TableCell>
                                        <TableCell align="right">Total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {despesasMes?.dias?.map((d) => (
                                        <TableRow hover key={d.data} onClick={() => onRowClick(d.data)} sx={{ cursor: 'pointer' }}>
                                            <TableCell>{d.data}</TableCell>
                                            <TableCell align="right">{currency(d.total)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="body2" color="text.secondary">Total do mês: <b>{currency(despesasMes?.total || 0)}</b></Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={7} lg={8}>
                    <Paper sx={{ p: 2, borderRadius: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>Detalhe do dia {selectedDate ? `(${selectedDate})` : ''}</Typography>
                        {loadingDia ? (
                            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
                        ) : !selectedDate ? (
                            <Typography variant="body2" color="text.secondary">Selecione um dia para ver as despesas do caminhão.</Typography>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Categoria</TableCell>
                                        <TableCell>Observação</TableCell>
                                        <TableCell align="right">Valor</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {despesasDia?.itens?.map((it) => (
                                        <TableRow key={it.id}>
                                            <TableCell>{it.categoria_descricao || it.categoria_codigo}</TableCell>
                                            <TableCell>{it.observacao || '-'}</TableCell>
                                            <TableCell align="right">{currency(it.valor)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="body2" color="text.secondary">Total do dia: <b>{currency(despesasDia?.total || 0)}</b></Typography>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );

    // ===== PAGAMENTOS VIEW =====
    const pagamentosView = (
        <Box sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="stretch">
                <Grid item xs={12} md={5} lg={4}>
                    <Paper sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle1">Pagamentos por dia</Typography>
                            <Stack direction="row" spacing={1}>
                                <Button size="small" onClick={() => pagamentosMes?.dias && exportToCsv(`pagamentos_${cicloId}.csv`, pagamentosMes.dias)}>Exportar CSV</Button>
                            </Stack>
                        </Box>
                        {loadingMes ? (
                            <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Data</TableCell>
                                        <TableCell align="right">Total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pagamentosMes?.dias?.map((d) => (
                                        <TableRow hover key={d.data} onClick={() => onRowClick(d.data)} sx={{ cursor: 'pointer' }}>
                                            <TableCell>{d.data}</TableCell>
                                            <TableCell align="right">{currency(d.total)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="body2" color="text.secondary">Total do mês: <b>{currency(pagamentosMes?.total || 0)}</b></Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={7} lg={8}>
                    <Paper sx={{ p: 2, borderRadius: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>Detalhe do dia {selectedDate ? `(${selectedDate})` : ''}</Typography>
                        {loadingDia ? (
                            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
                        ) : !selectedDate ? (
                            <Typography variant="body2" color="text.secondary">Selecione um dia para ver os pagamentos.</Typography>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Favorecido</TableCell>
                                        <TableCell>Forma</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Obs.</TableCell>
                                        <TableCell align="right">Valor</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pagamentosDia?.itens?.map((it) => (
                                        <TableRow key={it.id}>
                                            <TableCell>{it.favorecido || '-'}</TableCell>
                                            <TableCell>{it.forma_descricao || it.forma_codigo || '-'}</TableCell>
                                            <TableCell>{it.status}</TableCell>
                                            <TableCell>{it.observacao || '-'}</TableCell>
                                            <TableCell align="right">{currency(it.valor)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="body2" color="text.secondary">Total do dia: <b>{currency(pagamentosDia?.total || 0)}</b></Typography>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );

    return (
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', md: 1180 } } }}>
            {header}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                {tab === 'vendas' && vendasView}
                {tab === 'despesas' && despesasView}
                {tab === 'pagamentos' && pagamentosView}
            </Box>
        </Drawer>
    );
}
